import React, { memo, useEffect, useMemo, useRef, useState } from "react";

import styles from "./LineChartWithCustomLegend.module.scss";
import DashboardChart, {
  DashboardChartProps,
} from "app/screens/DashboardScreen/components/DashboardPremium/components/DashboardChart/DashboardChart";
import { ChartOptions } from "chart.js";
import Row from "app/components/Common/Row";
import { Col } from "antd";

type LineChartWithCustomLegendProps = DashboardChartProps & {
  toggleShowData: (index: number) => void;
};

export const LineChartWithCustomLegend = memo(
  ({ toggleShowData, ...props }: LineChartWithCustomLegendProps) => {
    const originalOptions = props.options;

    const chartRef = useRef(null);
    const legendContainerRef = useRef<HTMLDivElement>(null);
    const [legendHTML, setLegendHTML] = useState("");

    const options = useMemo<ChartOptions>(() => {
      return {
        ...originalOptions,
        legend: {
          display: false,
          labels: {
            boxWidth: 1,
          },
        },
        legendCallback: chart => {
          const text = [];
          chart.data.datasets.forEach(function (dataset, datasetIndex) {
            const backgroundColor = dataset.borderColor;
            const label = dataset.label;
            const isHidden = dataset.hidden;

            text.push(
              `<div class="legend-item" data-dataset-index="${datasetIndex}" style="display: flex; cursor: default; gap: 8px; align-items: baseline;">` +
                '<span style="background-color:' +
                backgroundColor +
                '; border-radius: 2px; width: 12px; height: 12px; aspect-ratio: 1;"></span>' + // Custom solid square
                (isHidden
                  ? '<span style="text-decoration: line-through;">'
                  : "<span>") +
                label +
                "</span></div>"
            );
          });
          return text.join("");
        },
      };
    }, [originalOptions]);

    useEffect(() => {
      if (chartRef.current) {
        const chartInstance = chartRef.current.chartInstance;

        const html = chartInstance.generateLegend();
        setLegendHTML(html);
      }
    }, [props.data, options]); // Re-run if chartData/options change

    // EFFECTIVE SOLUTION: useEffect 2: Attach click listeners to the HTML
    useEffect(() => {
      // This runs whenever legendHTML is updated (i.e., after the chart renders)
      if (
        legendContainerRef.current &&
        legendHTML &&
        legendContainerRef.current
      ) {
        // Select all custom legend list items
        const items =
          legendContainerRef.current.querySelectorAll(".legend-item");

        const clickHandler = event => {
          // Get the dataset index from the data attribute
          const datasetIndex =
            event.currentTarget.getAttribute("data-dataset-index");
          const index = parseInt(datasetIndex, 10);

          toggleShowData(index);
        };

        // Attach the handler to each item
        items.forEach(item => {
          item.addEventListener("click", clickHandler);
        });

        // CLEANUP FUNCTION: Remove listeners when the component unmounts or updates
        return () => {
          items.forEach(item => {
            item.removeEventListener("click", clickHandler);
          });
        };
      }
    }, [legendHTML, toggleShowData]); // DEPENDENCY: Rerun when the legend HTML updates

    return (
      <Row>
        <Col span={24} md={21} xl={22}>
          <DashboardChart {...props} options={options} lineRef={chartRef} />
        </Col>
        <Col span={24} md={3} xl={2}>
          <div
            ref={legendContainerRef}
            className={styles.legend}
            dangerouslySetInnerHTML={{ __html: legendHTML }}
          />
        </Col>
      </Row>
    );
  }
);
